import { VERDICT_WORD, type HabitPeriod, type HabitVerdict } from './api';
import { daysBetween } from './taskVocab';
import { WINDOW_WORDS, type FigureWindow } from './habitFigure';
import { readableDay } from './dateLabels';

/**
 * How a habit's streak is said, in one place.
 *
 * `STATE_LABEL`, `stateLabel` and the run's wording lived in `Habits.tsx`, so every other surface that
 * shows a habit either invented its own wording or said nothing. Today's pane printed
 * "2 in a row" — two of *what*, on a weekly habit — while the habits screen said "2 weeks in a
 * row"; and only the habits screen ever named the state at all.
 *
 * The state half of that has gone. There were two vocabularies for one fact — this one, keyed on
 * `HabitState`, and `VERDICT_WORD` in `api.ts`, keyed on the verdict the server sends — and they
 * disagreed about the case both existed to get right: `STATE_LABEL.on_track` said "on track"
 * about a habit with nothing logged, which is the sentence `too_early` was added to replace. Both
 * were reachable at once, because `verdict` was declared optional in the browser and every screen
 * fell back to a different one of them. `VERDICT_WORD` is now the only map, and it is mirrored
 * against core's `VERDICT_LABEL` by `verdictWordsMirrorCore.spec.ts` — the same shape `goalVocab`
 * uses for the stalled-goal wording, because the web bundle deliberately does not depend on
 * `@planner/core`.
 *
 * The same reasoning as `taskVocab.ts`, which exists because "next, but blocked by something"
 * needed to read identically on a row, a card and a picker.
 */

/** "4 days in a row" — and "1 day in a row", because a streak of one is the commonest kind. */
const runLabel = (period: HabitPeriod, count: number): string =>
  `${count} ${period}${count === 1 ? '' : 's'} in a row`;

/**
 * The window a run was counted over, said as a span: *90 days*, *12 weeks*, *12 months*.
 *
 * One spelling for the row's *best 4 in 90 days* and the run tile's tooltip (*in the last 90
 * days*), so the two cannot name different windows. The count is never typed here: the row takes it
 * from the service's `runWindow` and the tile from the periods the chart drew, and both of those
 * are `DEFAULT_PERIODS_FOR` in core — this bundle deliberately does not import core, so a copy of
 * the constant here would be the second place the window is decided.
 */
export const runWindowSpan = (periods: number, period: HabitPeriod): string =>
  `${periods} ${period}${periods === 1 ? '' : 's'}`;

/** What a payload has to carry for its run to be said. Every habit payload carries these. */
export interface RunFacts {
  currentRun: number;
  bestRun: number;
  /** How many periods both runs were counted over. */
  runWindow: number;
  isReading: boolean;
}

/**
 * A habit's run, said one way on the three screens that state it (`01a03813-f7d8`).
 *
 * Three authorings of one fact, measured on a daily habit with no run and a best of 12: the
 * history tile printed **`best 12`** under the label *In a row* — the record promoted into the
 * run's slot — the card's row printed *no run · best 12*, and Today's row printed nothing at all.
 * With a run of 5 they agreed (`5 · best 12`, `5 days in a row · best 12`, `5 days in a row`), so
 * a dead run of 0 and a live run of 12 read alike down a column of tiles, and the row that says
 * *no run* sat two lines above a tile that seemed to say 12.
 *
 * The empty state is a number, because a run of 0 is a fact about the habit and not the absence of
 * one. Per case —
 *
 * - **a live run** — `run` *5 days in a row*, `best` *best 12 in 90 days*, `tile` *5 · best 12*.
 * - **no run, a best** — *0 days in a row*, *best 12 in 90 days*, *0 · best 12*.
 * - **never a run** — *0 days in a row*, no best, *0*. A best of 0 is not a record, so it is not
 *   said; the tile, whose label already says *In a row*, is shown only where there is one.
 * - **abstinence** — the same words. Its run is of finished clean days (`forRuns`, which counts a
 *   limit only once the day is over), and `cleanDayCount` beside it on the card is the other number,
 *   said there with its own predicate (*20 clean days including today*).
 * - **a reading** — `null`. Readings were exempted from runs in Phase 66: a body fat kept under its
 *   ceiling is not a thing done in a row, and no surface invents one.
 *
 * The best names its window — *best 4 in 90 days*, *best 2 in 12 weeks* — because it is the best
 * run inside the window both runs were counted over, not a record (`01a15a31-62da`). The
 * maximizer's Sleep read a bare *best 4* over fifteen nights running in July, which had only fallen
 * out of the window; a reader takes *best* as a personal record, and only the tile's tooltip said
 * otherwise. Naming the window was chosen over widening `bestRun` to the whole record: the row and
 * the card are counted over one window, and a lifetime best would put a third on the same card.
 *
 * The tile keeps the bare count (*0 · best 4*): it sits under *In a row* with the window in its
 * tooltip, and it is the narrowest place a run is said. *When* the best was set is still not
 * something this can say — the service serves no date with it.
 */
export function runFigure(
  h: RunFacts,
  period: HabitPeriod,
): { run: string; best: string | null; tile: string } | null {
  if (h.isReading) return null;
  if (h.bestRun <= 0)
    return { run: runLabel(period, h.currentRun), best: null, tile: `${h.currentRun}` };
  return {
    run: runLabel(period, h.currentRun),
    best: `best ${h.bestRun} in ${runWindowSpan(h.runWindow, period)}`,
    tile: `${h.currentRun} · best ${h.bestRun}`,
  };
}

/** Which of the five drawings a period gets — the class suffix after `tone-`. */
export type PeriodTone = 'current' | 'inapplicable' | 'unmeasured' | 'met' | 'missed';

/**
 * One period's tone, for the 12-week chart and the row strip alike.
 *
 * The strip used to paint its own vocabulary — `is-met / is-partial / is-absent` — while the chart
 * one press below it painted this one, so the same weeks were two colours on one screen: a 16.4 t
 * week of an 18 t target was light green in the strip and orange in the chart, and `is-partial`
 * gave 58% and 99% the same green (`01a08096-e70c`). Both surfaces call this now, so they cannot
 * disagree about a period without one of them stopping calling it.
 *
 * The order is the chart's, and each step is argued where the chart draws it (`PeriodBars`):
 * `isCurrent` first, because "still running" is not a verdict; then the two sentences for "no habit
 * was running" — before it started, or paused — which share a drawing; then "nobody took a
 * reading"; and only then a verdict.
 */
export function periodTone(p: {
  isCurrent: boolean;
  applicable: boolean;
  unmeasured: boolean;
  paused: boolean;
  met: boolean;
}): PeriodTone {
  const beforeItExisted = !p.applicable && !p.unmeasured && !p.paused;
  if (p.isCurrent) return 'current';
  if (beforeItExisted || p.paused) return 'inapplicable';
  if (p.unmeasured) return 'unmeasured';
  return p.met ? 'met' : 'missed';
}

/**
 * Whether the period a row is judged over has ended on the account's calendar.
 *
 * `byDay` is the period the day on screen falls in — the day route builds it from the same bounds
 * the verdict was judged over — so its last day is the period's end. Today paged back to a finished
 * day is judged *as that day* (Phase 87, `01a03cd9-79a3`), which makes its period the current one
 * relative to the day on screen and lets a backfill land where the reader is looking. That stays.
 * What this answers is the other question, the one the words owe the reader: is the period over
 * *now*. A weekly habit viewed on Tuesday of this week is still running; Deep sleep on yesterday is
 * not, and *still running* / *behind* over it read as a day that could yet be saved.
 *
 * `today` is the account's — never the browser's or UTC's, which would put the boundary a day out
 * for most of the day far from Greenwich.
 */
export const periodIsOver = (byDay: readonly { day: string }[], today: string): boolean => {
  const end = byDay.at(-1)?.day;
  return end !== undefined && end < today;
};

/**
 * The verdict word for a period that has finished.
 *
 * The same verdict, said as a result rather than as a pace. The server's `behind` on a fully elapsed
 * period is a miss — there is no time left to be behind *in* — and its `too_early` on one is a
 * period nothing was logged in, not one it is too early to judge. `met` drops *at target*'s
 * present tense for the word the strip's cells already use. Every other verdict is already a
 * result (`over`, `just over`, `clean`) or not a verdict at all (`paused`), and keeps its word —
 * `on_pace` included, which a fully elapsed floor cannot reach: with no time left the tolerance is
 * zero, so anything short of the target is `behind` and anything at it `met`.
 */
const FINISHED_WORD: Partial<Record<HabitVerdict, string>> = {
  met: 'met',
  behind: 'missed',
  too_early: 'not logged',
};

/**
 * Whether a row's verdict is a period kept — the verdicts `verdictWord` says as *at target*, *met*
 * or *clean*, and no others.
 *
 * Today's *N/25 due today* counted `loggedOnDay > 0` (`01a15a31-35de`), so Deep sleep at 84 of a
 * 90-minute target counted as done while its own row read *on pace*, and a ceiling kept clean could
 * never count unless something was logged against it. The count now reads this, and so does
 * anything else that has to call a row done: one predicate over the verdict the row is worded from,
 * not a second reading of the figures. `verdictWord.spec` walks every verdict through both and
 * fails if they part.
 *
 * Not `verdictTone(...) === 'ok'`: that is green for `on_pace` too, which is the 84 of 90 case.
 * And a stale reading needs no branch here — it is only ever `too_early`, which is not kept.
 */
export const verdictIsMet = (verdict: HabitVerdict): boolean =>
  verdict === 'met' || verdict === 'clean';

/**
 * Days in a period, generously — a reading is stale once it is older than one whole period.
 *
 * A quarter because Reflection's row is judged over the review's window, which can be one (see
 * `verdictWord`); no habit has that cadence.
 */
const PERIOD_DAYS: Record<FigureWindow, number> = { day: 1, week: 7, month: 31, quarter: 92 };

/**
 * The window on screen, said from a later day: *that day*, *that week*. `WINDOW_WORDS`' family, for
 * a period that is over and is not the account's current one — see `verdictWord`'s *A past day*.
 */
const PAST_WINDOW_WORDS: Record<FigureWindow, string> = {
  day: 'that day',
  week: 'that week',
  month: 'that month',
  quarter: 'that quarter',
};

/** What a row has to carry for its verdict to be worded. Every habit payload carries these. */
export interface VerdictFacts {
  verdict: HabitVerdict;
  isReading: boolean;
  lastReadOn?: string | null;
}

/**
 * A reading nobody has taken for longer than its own period — the day it was last taken, or null.
 *
 * Only on `too_early`, which on a reading is the service's *unmeasured*: nothing was read this
 * period. A reading taken yesterday on a daily habit is one period old and the morning is still
 * young; one taken sixty days ago is not "too early to tell" about anything, and the distance to
 * the target beside it was measured from a number that no longer describes anyone.
 *
 * Measured from `asOf`, the day being judged — Today on a past date asks about that date.
 */
export function staleReadingSince(
  h: VerdictFacts,
  period: FigureWindow,
  asOf: string,
): string | null {
  if (!h.isReading || h.verdict !== 'too_early' || !h.lastReadOn) return null;
  return daysBetween(h.lastReadOn, asOf) > PERIOD_DAYS[period] ? h.lastReadOn : null;
}

/**
 * The verdict, in words, for one row — `VERDICT_WORD` unless the row has something truer to say.
 *
 * Two cases, both wording on the verdict the server sent and neither a new verdict (the pill's tone
 * still comes from `verdictTone(verdict)`):
 *
 * - **a stale reading** — *not read today* / *not read this week* / *not read this month*, ahead of
 *   everything, because *not logged* would hide that the gap is the reading's, not the day's. It
 *   replaces *too early to tell*, which said the opposite about a reading sixty days old
 *   (`01a15a31-5ea4`). On a day that is not the account's today the clause names the period on
 *   screen instead — see *A past day* below.
 * - **a finished period** — `periodOver`, from `periodIsOver`: *missed* / *met* / *not logged*.
 *
 * **No date in the stale clause.** The figure beside it already carries the reading's age —
 * *13.52 % 2 months ago*, Phase 108's `01a15691-8338` — and this clause used to say *no reading
 * since 18 Aug* after it, which is the age twice: the duplication `01a03813-b97d` removed from the
 * card, put back one clause later (`01a15d62-4f6d`). The architect's ruling keeps the age in the
 * figure and the period here. It holds only where a figure sits beside the word: a surface printing
 * this with no figure would want the date back in the clause, so check that before adding a caller.
 *
 * **A past day.** Today paged back is judged *as that day* (`day.ts`, `now: habitsAsOf`), so the
 * reading's staleness is as of the day on screen, and a reading taken this morning does not reach
 * yesterday's row. This clause used to say *not read today* there regardless, on the claim that it
 * was "as true of a finished day as of today" — true only until somebody read today, at which point
 * the row said *not read today* under the reading just taken (`01a15ee1-92a5`). With `today` passed
 * and `asOf` before it, the clause says *that day* / *that week* / *that month* once the period on
 * screen is over (`periodOver`), and *this week by 19 Oct* while it is still running, because the
 * week is today's but the judgement is the day's. The date there is the day judged, not the
 * reading's, so the rule below is not broken. A caller that omits `today` keeps the window words:
 * Reflection, whose *this week* is relative to the review shown, as its figure's already is.
 *
 * `period` is the window the verdict judged. That is the habit's cadence everywhere but Reflection,
 * whose row is judged over the review's window when the habit is shorter (`ReviewHabit.period`) —
 * a daily reading on a week review was found unread *this week*, and the row says so.
 */
export function verdictWord(
  h: VerdictFacts,
  opts: { period: FigureWindow; asOf: string; today?: string; periodOver?: boolean },
): string {
  if (staleReadingSince(h, opts.period, opts.asOf) !== null) {
    if (opts.today === undefined || opts.asOf >= opts.today) {
      return `not read ${WINDOW_WORDS[opts.period]}`;
    }
    // A day period on a day before today is always over, whatever `periodOver` was computed from.
    if (opts.periodOver || opts.period === 'day')
      return `not read ${PAST_WINDOW_WORDS[opts.period]}`;
    return `not read ${WINDOW_WORDS[opts.period]} by ${readableDay(opts.asOf)}`;
  }
  if (opts.periodOver) return FINISHED_WORD[h.verdict] ?? VERDICT_WORD[h.verdict];
  return VERDICT_WORD[h.verdict];
}
